import type { FormEvent, KeyboardEvent } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { getCategoryLabel } from '@/lib/categories'
import { useHelpIndex } from '@/lib/data'
import { ArrowRightIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'

import type { PublicHelpArticleSummary } from '@scrambl/shared/types/help-site'

const MAX_SUGGESTIONS = 6

/**
 * Instant title/keyword suggestions come from the already-loaded index (no
 * network); pressing Enter runs the full semantic search on /search.
 */
function suggest(
  articles: PublicHelpArticleSummary[],
  query: string,
): PublicHelpArticleSummary[] {
  const phrase = query.trim().toLowerCase()
  if (phrase.length < 2) return []
  const tokens = phrase.split(/\s+/).filter(Boolean)
  return articles
    .map(article => {
      const title = article.title.toLowerCase()
      const haystack = `${title} ${article.summary.toLowerCase()} ${article.keywords.join(' ')}`
      let score = 0
      if (title.includes(phrase)) score += 6
      else if (tokens.every(token => title.includes(token))) score += 4
      if (tokens.every(token => haystack.includes(token))) score += 2
      if (article.keywords.some(keyword => keyword.includes(phrase))) score += 1
      return { article, score }
    })
    .filter(row => row.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score || a.article.title.localeCompare(b.article.title),
    )
    .slice(0, MAX_SUGGESTIONS)
    .map(row => row.article)
}

export function SearchBox({
  compact = false,
  initialQuery = '',
  autoFocus = false,
}: {
  compact?: boolean
  initialQuery?: string
  autoFocus?: boolean
}) {
  const navigate = useNavigate()
  const { index } = useHelpIndex()
  const [query, setQuery] = useState(initialQuery)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const listId = useId()
  const rootRef = useRef<HTMLFormElement>(null)

  const suggestions = isOpen && index ? suggest(index.articles, query) : []

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function submit(event?: FormEvent) {
    event?.preventDefault()
    const trimmed = query.trim()
    if (!trimmed) return
    setIsOpen(false)
    navigate(`/search?q=${encodeURIComponent(trimmed)}`)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!suggestions.length) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex(previous => (previous + 1) % suggestions.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex(previous =>
        previous <= 0 ? suggestions.length - 1 : previous - 1,
      )
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      const active = suggestions[activeIndex]
      if (!active) return
      event.preventDefault()
      setIsOpen(false)
      navigate(`/articles/${encodeURIComponent(active.id)}`)
    } else if (event.key === 'Escape') {
      setIsOpen(false)
    }
  }

  return (
    <form
      ref={rootRef}
      className={`search ${compact ? 'search-compact' : 'search-hero'}`}
      role="search"
      onSubmit={submit}
    >
      <MagnifyingGlassIcon className="search-icon" size={compact ? 16 : 20} />
      <input
        type="search"
        value={query}
        placeholder={compact ? 'Search help…' : 'Search guides and answers'}
        aria-label="Search help articles"
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={suggestions.length > 0}
        autoComplete="off"
        autoFocus={autoFocus}
        onChange={event => {
          setQuery(event.target.value)
          setActiveIndex(-1)
          setIsOpen(true)
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
      />
      {!compact ? (
        <button type="submit" className="search-submit" aria-label="Search">
          <ArrowRightIcon size={18} weight="bold" />
        </button>
      ) : null}
      {suggestions.length > 0 ? (
        <ul className="search-suggestions" id={listId} role="listbox">
          {suggestions.map((article, position) => (
            <li
              key={article.id}
              role="option"
              aria-selected={position === activeIndex}
              className={position === activeIndex ? 'is-active' : undefined}
              onMouseEnter={() => setActiveIndex(position)}
              onMouseDown={event => {
                event.preventDefault()
                setIsOpen(false)
                navigate(`/articles/${encodeURIComponent(article.id)}`)
              }}
            >
              <span className="suggestion-title">{article.title}</span>
              <span className="suggestion-meta">
                {getCategoryLabel(article.moduleSlug)}
              </span>
            </li>
          ))}
          <li className="search-suggestions-footer" role="presentation">
            <button type="submit">
              See all results for “{query.trim()}” <ArrowRightIcon size={12} />
            </button>
          </li>
        </ul>
      ) : null}
    </form>
  )
}
